import React, { useEffect, useRef } from 'react';
import { X, Clock, ExternalLink } from 'lucide-react';
import { OrderTimelineContent } from './OrderTimelineContent';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface OrderTimelineModalProps {
  isOpen: boolean;
  orderNo: number;
  onClose: () => void;
}

export const OrderTimelineModal: React.FC<OrderTimelineModalProps> = ({ isOpen, orderNo, onClose }) => {
  const { t } = useTranslation();
  const overlayRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Close on overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
    >
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-6xl h-[90vh] flex flex-col overflow-hidden">
        {/* Modal header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white flex-shrink-0">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              {t('manager.modules.orderTimeline.modal.titleWithOrder', { '0': orderNo }, `Ordretidslinje – Ordre ${orderNo}`)}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => window.open(`${window.location.pathname}?module=order-timeline&orderNo=${orderNo}`, '_blank')}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title={t('manager.modules.orderTimeline.modal.openInNewWindow', {}, 'Åpne i nytt vindu')}
            >
              <ExternalLink className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal content (scrollable) */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          <OrderTimelineContent orderNo={orderNo} />
        </div>
      </div>
    </div>
  );
};
